import { clsx } from 'clsx';
import { selectEnv } from 'common-nextjs';
import React, { useEffect } from 'react';
import { CSSProp } from 'styled-components';
import { getAppleRedirectUrl } from '~/api/iam/auth/apple';
import useScript from '~/hooks/useScript';
import useAuthService, { AppleSignInError } from '~/lib/session/useAuthService';
import createSvg from '~/public/static/svg/createSvg';
import { AnalyticsSource } from '~/services/analytics/AnalyticsInterfaces';
import { SocialAuthCallback } from '~/typings/services/iam/auth';
import waitUntil from '~/utils/waitUntil';

interface Props {
  className?: string;
  css?: CSSProp;
  mode?: 'auth' | 'link';
  onSocialAuthNewUser?: SocialAuthCallback;
  onSocialAuthExistingEmail?: SocialAuthCallback;
  onSuccessfulLink?: () => void;
  onSuccessfulLogin?: () => void;
  src?: AnalyticsSource;
  text?: string;
}

const AppleLoginButton: React.FC<Props> = ({
  className,
  css,
  mode = 'auth',
  onSocialAuthNewUser,
  onSocialAuthExistingEmail,
  onSuccessfulLink,
  onSuccessfulLogin,
  text = 'Continue with Apple',
}) => {
  const [scriptLoaded] = useScript(
    'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js',
  );
  const authService = useAuthService();

  useEffect(() => {
    if (scriptLoaded) {
      waitUntil(() => typeof AppleID !== 'undefined', 50).then(() => {
        AppleID.auth.init({
          clientId: selectEnv(
            'com.sidelineswap.web',
            'com.sidelineswap.web.staging',
          ),
          redirectURI: getAppleRedirectUrl(),
          scope: 'name email',
          usePopup: true, //or false defaults to false
        });
      });
    }
  }, [scriptLoaded]);

  async function handleClick() {
    try {
      const response = await AppleID.auth.signIn();
      if (mode === 'auth') {
        await authService.trySocialAuth('Apple', {
          onSocialAuthExistingEmail,
          onSocialAuthNewUser,
          onSuccessfulLogin,
          appleAuthorizationCode: response.authorization.code,
          token: response.authorization.id_token,
        });
      } else if (mode === 'link') {
        await authService.linkSocialAccount('Apple', {
          onSuccessfulLink,
          appleAuthorizationCode: response.authorization.code,
          token: response.authorization.id_token,
        });
      }
    } catch (e) {
      throw new AppleSignInError((e as Error).message);
    }
  }

  return (
    <button
      className={clsx(
        'flex h-10 w-full items-center justify-center rounded px-4 text-sm font-semibold text-white',
        className,
      )}
      css={`
        background-color: #000;
        ${css}
      `}
      onClick={handleClick}
    >
      <AppleIcon />
      <div className="ml-4">{text}</div>
    </button>
  );
};

const AppleIcon = createSvg(props => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 1000 1187.2"
    {...props}
  >
    <path
      fill="currentColor"
      d="M979.04 925.19c-17.95 41.48-39.2 79.65-63.83 114.76-33.56 47.85-61.04 80.97-82.22 99.37-32.83 30.19-68 45.65-105.67 46.53-27.04 0-59.65-7.7-97.61-23.3-38.09-15.54-73.09-23.23-105.09-23.23-33.56 0-69.56 7.7-108.06 23.23-38.56 15.6-69.62 23.74-93.37 24.55-36.12 1.54-72.13-14.37-108.06-47.78-22.94-20-51.63-54.3-86-102.9-36.88-51.87-67.2-112.04-90.94-180.63C12.75 781.7 0 709.96 0 640.5c0-79.56 17.2-148.18 51.63-205.68 27.06-46.2 63.06-82.63 108.12-109.38 45.06-26.74 93.75-40.37 146.19-41.25 28.69 0 66.3 8.88 113.06 26.32 46.62 17.5 76.56 26.37 89.68 26.37 9.82 0 43.07-10.37 99.45-31.06 53.31-19.19 98.3-27.13 135.17-24 99.88 8.06 174.92 47.43 224.82 118.37-89.33 54.13-133.52 129.94-132.64 227.19.8 75.75 28.29 138.79 82.3 188.84 24.47 23.23 51.8 41.18 82.22 53.93a888.54 888.54 0 01-20.96 55.04zM749.96 23.75c0 59.37-21.69 114.81-64.92 166.12-52.18 61-115.29 96.25-183.73 90.69a184.84 184.84 0 01-1.38-22.5c0-57 24.82-118 68.88-167.87 22-25.25 49.98-46.25 83.9-63 33.87-16.5 65.89-25.63 96-27.19.89 7.94 1.25 15.88 1.25 23.75z"
    />
  </svg>
));

export default AppleLoginButton;
